import { createPortal } from 'react-dom';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';

import JxForm from '../../JxControls/JxForms/JxForm';
import JxTitleBar from '../../JxControls/JxTitleBar/JxTitleBar';
import JxButton from '../../JxControls/JxButtons/JxButton';
import { jxScrollbarThin } from '../../JxControls/jxScrollbarStyle';

import CreateNoteDlgBody from './CreateNoteDlgBody';
import { TITLE_BAR_ACTIONS } from './CreateNoteDlgTitleBarActions';
import { createSchema, createEmpty } from './createNoteSchema';
import newNoteSaver from './NewNoteSaver';

import '../../JxControls/JxTheme.css';
import './CreateNoteDlgWidget.css';

/**
 * CreateNoteDlgWidget — floating dialog for creating a new note.
 * Extends JxForm: drag + bringToTop + Escape-to-close + Joi validation.
 *
 * Inputs are initialized from anchorEntry when dialog opens.
 * onSave is intentionally absent — save logic will be handled by NewNoteSaver once built.
 *
 * @param {boolean}  props.open
 * @param {object}   props.anchorEntry  - prefills fields (service, cat, type, etc.)
 * @param {function} props.onClose
 */
export default class CreateNoteDlgWidget extends JxForm {
    constructor(props) {
        super(props);
        this.name = 'CreateNoteDlgWidget';
        this.schema = createSchema();
        this.state = { ...this.state, inputs: createEmpty(props.anchorEntry) };
    }

    getObjectType() {
        return 'NewNote';
    }

    // Literal, hardcoded default — no window.innerWidth centering math. width/height are
    // not read by panelStyle (this is a JxForm, non-resizable, CSS defines the real width)
    // but every window returns the full 4-field shape for geo consistency.
    getDefaultGeo() {
        return { top: 120, left: 610, width: 480, height: 400 };
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);

        if (this.props.open && !prevProps.open) {
            this.setState({ inputs: createEmpty(this.props.anchorEntry) });
        }
    }

    handleClose = async (action) => {
        try {
            if (action === 0) {
                const errors = this.validate();
                if (errors !== null) return;

                await newNoteSaver.save(this.state.inputs);
            }
            this.props.onClose?.();
        } catch (error) {
            this.logError('handleClose', error.message);
        }
    };

    render() {
        if (!this.props.open) return null;

        return createPortal(
            <div {...this.panelProps} style={this.panelStyle} className={`cndw-panel ${this.panelActiveClass}`}>
                <JxTitleBar
                    id={`${this.name}_titlebar`}
                    title="New Note"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={TITLE_BAR_ACTIONS(() => this.handleClose(-1))}
                    icon={<NoteAddOutlinedIcon sx={{ fontSize: 13 }} />}
                />

                <div className={`cndw-body ${jxScrollbarThin}`}>
                    <CreateNoteDlgBody inputs={this.state.inputs} errors={this.state.errors} onChange={this.handleChange} />
                </div>

                <div className="cndw-footer">
                    <JxButton label="Cancel" variant="secondary" onClick={() => this.handleClose(-1)} />
                    <JxButton label="Save" variant="primary" onClick={() => this.handleClose(0)} />
                </div>
            </div>,
            document.body,
        );
    }
}
